import { calculateTeamBasedElo } from './courtUtils';
import { computeSinglesEloResults } from './singlesEloCalculation';
import { resolveEloPolicy } from './eloUtils';

const DEFAULT_ELO = 1200;
const UNBALANCED_GAME_TYPE_IDS = new Set([6, 8, 9]);
const SINGLES_GAME_TYPE_IDS = new Set([4, 5, 7]);

function gameCreatedAtMs(game) {
  const raw = game?.created_at;
  const ms = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(ms) ? ms : 0;
}

function laterGamesInSession(game, allSessionGames = []) {
  const gameId = game?.id;
  const t = gameCreatedAtMs(game);
  return (allSessionGames || []).filter((other) => {
    if (!other || other.id === gameId) return false;
    return gameCreatedAtMs(other) > t;
  });
}

function eloRowForPlayer(game, playerId) {
  return (game?.elo_history || []).find((row) => row.player_id === playerId) || null;
}

export const SCORE_CORRECTION_POLICY_TOOLTIP =
  'Scores can be edited while the session is live, or shortly after it ends.';

export const POST_SESSION_CORRECTION_WINDOW_HOURS = 72;

/**
 * Tooltip copy when the Edit score action is disabled.
 * @param {string|null|undefined} reason
 * @returns {string}
 */
export function getEditScoreDisabledTooltip(reason) {
  if (!reason) return SCORE_CORRECTION_POLICY_TOOLTIP;
  return reason;
}

/**
 * Whether a host may correct this competitive game.
 * Live: any game. Last-game uses the delta path; older games use chain replay.
 * Post-session: any game when completed + latest club + window.
 * @param {object} game
 * @param {{
 *   sessionStatus?: string,
 *   allSessionGames?: object[],
 *   playersById?: object,
 *   isLatestClubSession?: boolean,
 *   withinCorrectionWindow?: boolean,
 * }} [options]
 * @returns {{ eligible: boolean, reason: string|null, mode: 'live'|'live_chain'|'post_session'|null }}
 */
export function checkGameCorrectionEligibility(game, options = {}) {
  const {
    sessionStatus,
    allSessionGames = [],
    isLatestClubSession = false,
    withinCorrectionWindow = false,
  } = options;

  if (!game?.id) {
    return { eligible: false, reason: 'Game not found.', mode: null };
  }

  const participants = game.players_session_games || [];
  if (!participants.length) {
    return { eligible: false, reason: 'This game has no players to correct.', mode: null };
  }

  if (sessionStatus === 'completed') {
    if (!isLatestClubSession) {
      return {
        eligible: false,
        reason: 'Scores can only be edited on the club’s most recent session.',
        mode: null,
      };
    }
    if (!withinCorrectionWindow) {
      return {
        eligible: false,
        reason: `The ${POST_SESSION_CORRECTION_WINDOW_HOURS}-hour score correction window for this session has ended.`,
        mode: null,
      };
    }
    return { eligible: true, reason: null, mode: 'post_session' };
  }

  if (sessionStatus !== 'ongoing') {
    return {
      eligible: false,
      reason: 'Scores can only be edited while the session is live, or shortly after it ends.',
      mode: null,
    };
  }

  const later = laterGamesInSession(game, allSessionGames);
  let needsChain = false;

  for (const psg of participants) {
    const playerId = psg.player_id;
    const thisElo = eloRowForPlayer(game, playerId);

    if (thisElo) {
      const pairType = thisElo.pair_type;
      const laterRated = later.find((other) =>
        (other.elo_history || []).some(
          (eh) => eh.player_id === playerId && eh.pair_type === pairType
        )
      );
      if (laterRated) {
        needsChain = true;
        break;
      }
    } else {
      const laterPlayed = later.find((other) =>
        (other.players_session_games || []).some((row) => row.player_id === playerId)
      );
      if (laterPlayed) {
        needsChain = true;
        break;
      }
    }
  }

  return {
    eligible: true,
    reason: null,
    mode: needsChain ? 'live_chain' : 'live',
  };
}

function gamesPlayedKeyForPairType(pairType) {
  if (pairType === 'mixed') return 'mixed_games_played';
  if (pairType === 'singles') return 'singles_games_played';
  return 'levels_games_played';
}

function remainingRatedGamesInclusive(playerId, pairType, game, allSessionGames) {
  const t = gameCreatedAtMs(game);
  return (allSessionGames || []).filter((other) => {
    if (!other) return false;
    if (gameCreatedAtMs(other) < t) return false;
    return (other.elo_history || []).some(
      (eh) => eh.player_id === playerId && eh.pair_type === pairType
    );
  }).length;
}

function seedPlayerFromHistory(playerId, game, playersById = {}, options = {}) {
  const live = playersById[playerId] || {};
  const eh = eloRowForPlayer(game, playerId);
  const pairType = eh?.pair_type;
  const oldValue =
    options.ratingOverride != null
      ? Number(options.ratingOverride)
      : eh?.old_value == null
        ? DEFAULT_ELO
        : Number(eh.old_value);

  const gpKey = gamesPlayedKeyForPairType(pairType);
  let gamesPlayedEntering = Math.max(0, (Number(live[gpKey]) || 0) - (pairType ? 1 : 0));
  if (options.allSessionGames && pairType) {
    gamesPlayedEntering = Math.max(
      0,
      (Number(live[gpKey]) || 0) -
        remainingRatedGamesInclusive(playerId, pairType, game, options.allSessionGames)
    );
  }

  return {
    id: playerId,
    gender: live.gender,
    mixed_elo_rating: pairType === 'mixed' ? oldValue : (live.mixed_elo_rating ?? DEFAULT_ELO),
    levels_elo_rating: pairType === 'levels' ? oldValue : (live.levels_elo_rating ?? DEFAULT_ELO),
    singles_elo_rating: pairType === 'singles' ? oldValue : (live.singles_elo_rating ?? DEFAULT_ELO),
    mixed_games_played:
      pairType === 'mixed' ? gamesPlayedEntering : Math.max(0, Number(live.mixed_games_played) || 0),
    levels_games_played:
      pairType === 'levels' ? gamesPlayedEntering : Math.max(0, Number(live.levels_games_played) || 0),
    singles_games_played:
      pairType === 'singles' ? gamesPlayedEntering : Math.max(0, Number(live.singles_games_played) || 0),
    pair_type: pairType,
    old_value: oldValue,
    previous_change: eh?.elo_change == null ? 0 : Number(eh.elo_change),
  };
}

function teamIdsFromGame(game, teamNumber) {
  return (game?.players_session_games || [])
    .filter((row) => row.team_number === teamNumber)
    .map((row) => row.player_id)
    .filter(Boolean);
}

/**
 * Recompute post-game ratings from stored elo_history.old_value snapshots.
 * @returns {{
 *   updates: { player_id: string, pair_type: string, new_value: number }[],
 *   preview: { player_id: string, pair_type: string, oldChange: number, newChange: number, newValue: number }[],
 *   isEloExempt: boolean
 * }}
 */
export function buildCorrectedEloPayload(game, newScores, playersById = {}, options = {}) {
  return recomputeGameElo(game, newScores, playersById, options);
}

function passThroughEloFromHistory(game, options = {}) {
  const ratingOverrides = options.ratingOverrides || {};
  const updates = [];
  const preview = [];

  (game?.elo_history || []).forEach((eh) => {
    if (!eh?.player_id || !eh?.pair_type) return;
    const oldValue =
      ratingOverrides[eh.player_id] != null
        ? Number(ratingOverrides[eh.player_id])
        : eh.old_value == null
          ? DEFAULT_ELO
          : Number(eh.old_value);
    updates.push({
      player_id: eh.player_id,
      pair_type: eh.pair_type,
      new_value: oldValue,
    });
    preview.push({
      player_id: eh.player_id,
      pair_type: eh.pair_type,
      oldChange: eh.elo_change == null ? 0 : Number(eh.elo_change),
      newChange: 0,
      newValue: oldValue,
    });
  });

  return { updates, preview, isEloExempt: true };
}

function recomputeGameElo(game, newScores, playersById = {}, options = {}) {
  const history = game?.elo_history || [];
  const team1Score = Number(newScores?.team1Score);
  const team2Score = Number(newScores?.team2Score);
  const team1Won = team1Score > team2Score;
  const gameTypeId = Number(game?.game_type_id);
  const ratingOverrides = options.ratingOverrides || {};
  const eloPolicy = resolveEloPolicy(game?.created_at);

  if (!history.length) {
    return { updates: [], preview: [], isEloExempt: true };
  }

  if (UNBALANCED_GAME_TYPE_IDS.has(gameTypeId)) {
    return passThroughEloFromHistory(game, options);
  }

  const seedOpts = (playerId) => ({
    ratingOverride: ratingOverrides[playerId],
    allSessionGames: options.allSessionGames,
  });

  const team1Ids = teamIdsFromGame(game, 1);
  const team2Ids = teamIdsFromGame(game, 2);

  if (SINGLES_GAME_TYPE_IDS.has(gameTypeId) && team1Ids.length === 1 && team2Ids.length === 1) {
    const p1 = seedPlayerFromHistory(team1Ids[0], game, playersById, seedOpts(team1Ids[0]));
    const p2 = seedPlayerFromHistory(team2Ids[0], game, playersById, seedOpts(team2Ids[0]));
    const results = computeSinglesEloResults(p1, p2, {
      team1Won,
      team1Score,
      team2Score,
      gameTypeId,
      eloPolicy,
    });

    const preview = [];
    const updates = [];
    [p1, p2].forEach((player) => {
      const result = results[player.id];
      if (!result || !player.pair_type) return;
      const newValue = Math.round(result.newRating);
      const newChange = newValue - player.old_value;
      updates.push({
        player_id: player.id,
        pair_type: player.pair_type,
        new_value: newValue,
      });
      preview.push({
        player_id: player.id,
        pair_type: player.pair_type,
        oldChange: player.previous_change,
        newChange,
        newValue,
      });
    });
    return { updates, preview, isEloExempt: false };
  }

  if (team1Ids.length !== 2 || team2Ids.length !== 2) {
    return { updates: [], preview: [], isEloExempt: true };
  }

  const team1Players = team1Ids.map((id) =>
    seedPlayerFromHistory(id, game, playersById, seedOpts(id))
  );
  const team2Players = team2Ids.map((id) =>
    seedPlayerFromHistory(id, game, playersById, seedOpts(id))
  );
  const isMixed = gameTypeId === 1;
  const results = calculateTeamBasedElo(
    team1Players,
    team2Players,
    team1Won,
    0,
    isMixed,
    { eloPolicy, team1Score, team2Score }
  );

  const preview = [];
  const updates = [];
  results.forEach((player) => {
    const seeded = [...team1Players, ...team2Players].find((p) => p.id === player.id);
    if (!seeded?.pair_type) return;
    const ratingKey = isMixed ? 'mixed_elo_rating' : 'levels_elo_rating';
    const newValue = Math.round(player[ratingKey]);
    const newChange = newValue - seeded.old_value;
    updates.push({
      player_id: player.id,
      pair_type: seeded.pair_type,
      new_value: newValue,
    });
    preview.push({
      player_id: player.id,
      pair_type: seeded.pair_type,
      oldChange: seeded.previous_change,
      newChange,
      newValue,
    });
  });

  return { updates, preview, isEloExempt: false };
}

function sortSessionGames(allSessionGames = []) {
  return [...(allSessionGames || [])].sort((a, b) => gameCreatedAtMs(a) - gameCreatedAtMs(b));
}

function hasEloHistory(game) {
  return (game?.elo_history || []).length > 0;
}

function isUnbalancedGame(game) {
  return UNBALANCED_GAME_TYPE_IDS.has(Number(game?.game_type_id));
}

/**
 * Recompute the edited game plus every later rated game in the session.
 * @returns {{
 *   eloUpdates: object[],
 *   preview: object[],
 *   isEloExempt: boolean,
 *   chainUpdates: { session_game_id: string, players: object[] }[],
 *   laterGamesCount: number,
 *   playerCount: number,
 *   remainingRatedGames: number,
 * }}
 */
export function buildSessionChainReplay(correctedGame, newScores, allSessionGames = [], playersById = {}) {
  const sorted = sortSessionGames(allSessionGames.length ? allSessionGames : [correctedGame]);
  const first = recomputeGameElo(correctedGame, newScores, playersById, {
    allSessionGames: sorted,
  });

  const running = {};
  const affectedPlayers = new Set();
  first.updates.forEach((row) => {
    running[`${row.player_id}:${row.pair_type}`] = row.new_value;
    affectedPlayers.add(row.player_id);
  });

  const later = laterGamesInSession(correctedGame, sorted);
  const remainingRatedGames = later.filter(hasEloHistory).length;
  const chainUpdates = [];

  later.forEach((game) => {
    if (!hasEloHistory(game)) return;

    const ratingOverrides = {};
    (game.elo_history || []).forEach((eh) => {
      const key = `${eh.player_id}:${eh.pair_type}`;
      if (running[key] != null) {
        ratingOverrides[eh.player_id] = running[key];
      }
    });

    const result = isUnbalancedGame(game)
      ? passThroughEloFromHistory(game, { ratingOverrides })
      : recomputeGameElo(
          game,
          { team1Score: game.team_1_score, team2Score: game.team_2_score },
          playersById,
          { ratingOverrides, allSessionGames: sorted }
        );
    if (!result.updates.length) return;

    const players = result.updates.map((row) => {
      const eh = eloRowForPlayer(game, row.player_id);
      const key = `${row.player_id}:${row.pair_type}`;
      const oldValue =
        running[key] != null
          ? running[key]
          : eh?.old_value == null
            ? DEFAULT_ELO
            : Number(eh.old_value);
      running[key] = row.new_value;
      affectedPlayers.add(row.player_id);
      return {
        player_id: row.player_id,
        pair_type: row.pair_type,
        old_value: oldValue,
        new_value: row.new_value,
      };
    });

    chainUpdates.push({ session_game_id: game.id, players });
  });

  const laterGamesCount = chainUpdates.length;
  const playerCount = affectedPlayers.size;

  return {
    eloUpdates: first.updates,
    preview: first.preview,
    isEloExempt: first.isEloExempt,
    chainUpdates,
    laterGamesCount,
    playerCount,
    remainingRatedGames,
  };
}

/**
 * Host-facing blast-radius counts for post-session confirmation.
 */
export function buildSessionChainReplayPreview(
  correctedGame,
  newScores,
  allSessionGames = [],
  playersById = {}
) {
  const replay = buildSessionChainReplay(correctedGame, newScores, allSessionGames, playersById);
  return {
    laterGamesCount: replay.laterGamesCount,
    playerCount: replay.playerCount,
    remainingRatedGames: replay.remainingRatedGames,
    isEloExempt: replay.isEloExempt,
  };
}
